import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Mail, Users, Send, Loader2, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

const InviteAdminModal = ({ isOpen, onClose, companyId, companyName }) => {
  const { isDemoCompany } = useAuth()
  const isDemo = isDemoCompany()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('Manager')
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [recentInvites, setRecentInvites] = useState([])
  const [showRecentInvites, setShowRecentInvites] = useState(false)

  // Load recent invitations when modal opens
  const loadRecentInvites = async () => {
    if (!companyId) return
    
    const { data, error } = await supabase
      .from('company_invitations')
      .select('id, email, role, status, created_date')
      .eq('company_id', companyId)
      .order('created_date', { ascending: false })
      .limit(5)
    
    if (!error && data) {
      setRecentInvites(data)
      setShowRecentInvites(data.length > 0)
    }
  }

  // Send invitation
  const handleSendInvitation = async () => {
    setError('')
    setSuccess(false)
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }
    
    setIsLoading(true)
    
    try {
      console.log('📧 Sending invitation with:', { email, role, message, companyId })
      
      // Call the edge function to send invitation
      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: {
          email,
          role,
          message,
          companyId
        }
      })
      
      console.log('📨 Edge function response:', { data, error })
      
      if (error) throw error
      
      if (data?.success) {
        setSuccess(true)
        setEmail('')
        setMessage('')
        setRole('Manager')
        
        // Reload recent invites
        await loadRecentInvites()
        
        // Auto close after 2 seconds
        setTimeout(() => {
          onClose()
          setSuccess(false)
        }, 2000)
      } else {
        throw new Error(data?.error || 'Failed to send invitation')
      }
    } catch (err) {
      console.error('❌ Error sending invitation:', err)
      console.error('Error details:', {
        message: err.message,
        error: err,
        companyId,
        role,
        email
      })
      setError(err.message || 'Failed to send invitation. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Cancel invitation
  const handleCancelInvitation = async (inviteId) => {
    try {
      const { error } = await supabase
        .from('company_invitations')
        .update({ status: 'cancelled' })
        .eq('id', inviteId)
      
      if (!error) {
        await loadRecentInvites()
      }
    } catch (err) {
      console.error('Error cancelling invitation:', err)
    }
  }

  // Handle modal open
  const handleModalOpen = () => {
    if (isOpen) {
      loadRecentInvites()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={handleModalOpen}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Invite Team Member to {companyName}
          </DialogTitle>
          <DialogDescription>
            Send an invitation to add a new team member to your company. They'll receive an email with instructions to join.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Success Alert */}
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Invitation sent successfully! The recipient will receive an email shortly.
              </AlertDescription>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Demo Mode Alert */}
          {isDemo && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Demo Mode:</strong> Invitations cannot be sent in demo mode.
                To invite real team members, please upgrade to a full account.
              </AlertDescription>
            </Alert>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role">User Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isLoading}>
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Admin">
                  <div>
                    <div className="font-medium">Admin</div>
                    <div className="text-xs text-muted-foreground">Full system access and configuration</div>
                  </div>
                </SelectItem>
                <SelectItem value="Manager">
                  <div>
                    <div className="font-medium">Manager</div>
                    <div className="text-xs text-muted-foreground">Manage training, assign tasks, and view reports</div>
                  </div>
                </SelectItem>
                <SelectItem value="User">
                  <div>
                    <div className="font-medium">User</div>
                    <div className="text-xs text-muted-foreground">Complete training and track personal compliance</div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Personal Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Personal Message (Optional)</Label>
            <Textarea
              id="message"
              placeholder="Add a personal message to include in the invitation email..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading}
              rows={3}
            />
          </div>

          {/* Recent Invitations */}
          {showRecentInvites && (
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-sm text-muted-foreground">Recent Invitations</Label>
              <div className="space-y-2">
                {recentInvites.map((invite) => (
                  <div key={invite.id} className="flex items-center justify-between p-2 bg-muted rounded-md">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{invite.email}</span>
                      <Badge variant={invite.status === 'pending' ? 'secondary' : 'outline'}>
                        {invite.status}
                      </Badge>
                      <Badge variant="outline">{invite.role}</Badge>
                    </div>
                    {invite.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelInvitation(invite.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvitation}
            disabled={isLoading || !email || isDemo}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : isDemo ? (
              <>
                <AlertCircle className="w-4 h-4 mr-2" />
                Demo Mode
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Invitation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default InviteAdminModal